import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, Construction } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import ZeloLogo from "./ZeloLogo";

const navItems = [
  { to: "/retail-leasing", label: "Retail Leasing" },
  { to: "/pre-leased", label: "Pre-Leased", badge: "Beta" },
  { to: "/broker", label: "For Brokers" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, setLoginOpen, logout } = useAuth();

  return (
    <header className="sticky top-0 z-40 glass border-b border-neutral-200/70" data-testid="site-header">
      <div className="container-zelo flex items-center h-20 md:h-24 gap-4">
        <ZeloLogo size="lg" to="/" className="mr-2" />

        <nav className="hidden md:flex items-center gap-1 mx-auto" data-testid="header-nav">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 inline-flex items-center gap-1.5 ${
                  isActive ? "text-brand bg-brand-50" : "text-neutral-700 hover:text-brand hover:bg-brand-50/60"
                }`
              }
              data-testid={`header-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
              {n.badge && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{n.badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 ml-auto">
          <button
            onClick={() => navigate("/list-your-property")}
            data-testid="header-list-property-btn"
            className="group inline-flex items-center gap-2 rounded-full pl-4 pr-2 py-2 text-sm font-semibold text-white bg-brand shadow-brand hover:bg-brand-600 transition-all duration-300 hover:scale-[1.02] active:scale-95"
          >
            <span>List Your Property</span>
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/20 group-hover:bg-white/30 transition">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </button>

          {user ? (
            <button
              onClick={logout}
              data-testid="header-logout-btn"
              className="px-4 py-2 text-sm font-semibold rounded-full border border-neutral-200 hover:border-brand hover:text-brand transition"
            >
              Hi, {user.first_name || user.phone}
            </button>
          ) : (
            <button
              onClick={() => setLoginOpen(true)}
              data-testid="header-login-btn"
              className="px-4 py-2 text-sm font-semibold rounded-full border border-neutral-200 hover:border-brand hover:text-brand transition"
            >
              Login
            </button>
          )}
        </div>

        <button
          className="md:hidden ml-auto p-2 rounded-lg border border-neutral-200"
          onClick={() => setOpen((v) => !v)}
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
          <div className="container-zelo py-4 flex flex-col gap-1">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg text-sm font-medium hover:bg-brand-50 flex items-center gap-2"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
                {n.badge && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{n.badge}</span>}
              </Link>
            ))}
            <div className="flex gap-2 pt-3">
              <button
                onClick={() => { setOpen(false); navigate("/list-your-property"); }}
                className="flex-1 rounded-full py-2 text-sm font-semibold text-white bg-brand"
                data-testid="mobile-list-property-btn"
              >
                List Your Property
              </button>
              {user ? (
                <button onClick={() => { setOpen(false); logout(); }} className="flex-1 rounded-full py-2 text-sm font-semibold border border-neutral-200">
                  Logout
                </button>
              ) : (
                <button onClick={() => { setOpen(false); setLoginOpen(true); }} className="flex-1 rounded-full py-2 text-sm font-semibold border border-neutral-200" data-testid="mobile-login-btn">
                  Login
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
